"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
export default function DigitalDominationStartsHere() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [isHoveringCTA, setIsHoveringCTA] = useState(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = container.clientWidth;
    let height = container.clientHeight;
    let animationFrameId: number;
    const handleResize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    const particleCount = Math.min(100, width * height / 15000);
    const connectionDistance = 150;
    const mouseDistance = 200;
    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      baseVx: number;
      baseVy: number;
    }
    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        baseVx: (Math.random() - 0.5) * 0.5,
        baseVy: (Math.random() - 0.5) * 0.5
      });
    }
    let mouseX = -1000;
    let mouseY = -1000;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", () => {
      mouseX = -1000;
      mouseY = -1000;
    });
    const render = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < mouseDistance) {
          const forceDirectionX = dx / distance;
          const forceDirectionY = dy / distance;
          const force = (mouseDistance - distance) / mouseDistance;
          const repelStrength = 2;
          p.vx += forceDirectionX * force * repelStrength * 0.1;
          p.vy += forceDirectionY * force * repelStrength * 0.1;
        }
        if (isHoveringCTA) {
          const centerX = width / 2;
          const centerY = height / 2;
          const rdx = p.x - centerX;
          const rdy = p.y - centerY;
          const rDist = Math.sqrt(rdx * rdx + rdy * rdy);
          const waveForce = Math.sin(Date.now() / 200 - rDist / 100) * 2;
          if (rDist < 600) {
            p.vx += rdx / rDist * waveForce * 0.05;
            p.vy += rdy / rDist * waveForce * 0.05;
          }
        }
        p.vx = p.vx * 0.98 + p.baseVx * 0.02;
        p.vy = p.vy * 0.98 + p.baseVy * 0.02;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(30, 41, 59, 0.2)";
        ctx.fill();
      });
      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            const opacity = 1 - dist / connectionDistance;
            ctx.strokeStyle = `rgba(30, 41, 59, ${opacity * 0.15})`;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(render);
    };
    render();
    return () => {
      window.removeEventListener("resize", handleResize);
      container.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isHoveringCTA]);
  return <section data-section-id="364" id="the-word-alchemist" ref={containerRef} className="bg-background text-foreground relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden py-20 px-4">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none" style={{
      opacity: 0.4
    }} />

      {}
      <div className="relative z-10 max-w-5xl mx-auto text-center space-y-8">
        {}
        <h1 className="font-serif text-5xl md:text-7xl lg:text-[84px] font-bold leading-[1.1] tracking-tighter text-primary drop-shadow-sm">
          From Complexity to Clarity
        </h1>

        {}
        <p className="font-serif text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Boutique copywriting and content strategy for digital leaders. 
          <br className="hidden md:block" />
          We translate technical jargon into human connection and business growth.
        </p>

        {}
        <div className="pt-8" onMouseEnter={() => setIsHoveringCTA(true)} onMouseLeave={() => setIsHoveringCTA(false)}>
          <Link to="/AboutKaleighPage">
            <Button size="lg" className="h-14 px-10 text-lg rounded-sm bg-primary text-primary-foreground hover:bg-primary/95 shadow-md transition-all duration-300 hover:scale-105">
              Start Your Story
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>

      {}
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none" />
    </section>;
}